// Lexikalische Duplikat-Erkennung über normalisierte Summary-Tokens.
// Bewusst rein lokal, ohne externe APIs — fängt Tippfehler, leichte
// Umformulierungen und Status-Suffixe ("X klemmt" vs "X klemmt nach
// Update"). Echte semantische Paraphrasen finden wir damit nicht.

import type { Issue } from '../types/issue';
import { resolvedOf } from './jira-dates';

// Deutsche Stopwörter — typische Schueco-Bug-Floskeln, die kein Signal
// tragen. Bewusst KEIN "tür/fenster/fassade" rein, weil das die
// systemtragenden Tokens sind, an denen wir Duplikate erkennen.
const STOPWORDS = new Set([
  'der','die','das','den','dem','des','ein','eine','einen','einem','eines','einer',
  'und','oder','aber','doch','sowie','bzw','sondern',
  'mit','ohne','für','von','vom','zu','zur','zum','im','in','an','am','auf','aus','bei','beim','nach','vor','über','unter','durch','gegen',
  'ist','sind','war','waren','wird','werden','wurde','wurden','sein','sei','gewesen','wäre','wären',
  'kann','könnte','muss','müsste','soll','sollte','will','würde','darf',
  'hat','haben','hatte','hatten','habe',
  'nicht','kein','keine','keinen','keiner','keinem','keines','nichts',
  'auch','noch','schon','wieder','immer','manchmal','oft','nie','nun','jetzt','hier','dort','da','dann','also','sowieso',
  'wenn','weil','dass','damit','obwohl','während','sobald','solange','falls',
  'man','sich','er','sie','es','wir','ihr','ihn','ihm','ihnen','seinem','seine','seiner','ihrer','ihren','ihre',
  'mein','dein','sein','unser','euer',
  'ja','nein','vielleicht',
  // generische Bug-Vokabeln, die nichts unterscheiden
  'bug','issue','problem','fehler','ticket','task','story','bei','beim','geht','gehen','funktioniert','works','nicht-funktioniert',
  // einzelne Buchstaben/Zahlen, die nach Split übrig bleiben
  'b','d','m','n','st','er','en',
]);

// System-Extractor: der erste Teil einer Summary vor der ersten „(nummer)",
// „ − ", „:" oder Ähnlichem ist bei euch üblicherweise die Produkt-Serie
// (z. B. „AWS 75 WF.SI+", „ADS 65", „Janisol Arte 2.0"). Wenn zwei Tickets
// mit lexikalisch fast identischen Summaries aber unterschiedlichen Systemen
// gemeldet sind, sind sie fachlich KEINE Duplikate.
//
// Beispiel:
//   „AWS 75 WF.SI+ (47) - Fixed field - …" → „aws 75 wf.si+"
//   „AWS 75.SI+ (245) - Fixed field - …"   → „aws 75.si+"   ← unterschiedlich
//
// Rückgabe null bei Summaries ohne erkennbares Präfix — dann greift der
// System-Filter nicht (defensive: keine falsch-negativen Duplikate).
const SYSTEM_SPLIT_RE = /^(.*?)(?:\s*\((?:\d+|nr\.?\s*\d+)\)|\s+[-–—]\s|\s*:\s)/i;

export function extractSystem(summary: string | undefined | null): string | null {
  if (!summary) return null;
  const m = summary.match(SYSTEM_SPLIT_RE);
  if (!m) return null;
  const cand = m[1].trim();
  if (cand.length < 3) return null;
  // Normalisieren: lowercase, Mehrfach-Whitespace kollabieren
  return cand.toLowerCase().replace(/\s+/g, ' ');
}

// Tokenizer: lowercase, deutsche Umlaute beibehalten, Punktuation raus,
// Tokens mit ≥3 Zeichen, keine reinen Zahlen.
export function tokenize(text: string): string[] {
  if (!text) return [];
  const lower = text.toLowerCase()
    // CAL-Issue-Keys im Text (z.B. "siehe CAL-12345") rausnehmen —
    // die sind als Inline-Verweis kein Signal für Themen-Duplikate.
    .replace(/\bcal-\d+\b/g, ' ')
    // typische JIRA-Markup-Reste
    .replace(/[\[\]{}()<>|"'`#*_~=]/g, ' ')
    // Trennzeichen
    .replace(/[\/\\,;:!?.\-–—]+/g, ' ');
  const raw = lower.split(/\s+/).filter(Boolean);
  const out: string[] = [];
  for (const t of raw) {
    if (t.length < 3) continue;
    if (/^\d+$/.test(t)) continue;          // reine Zahlen
    if (STOPWORDS.has(t)) continue;
    out.push(t);
  }
  return out;
}

export type DuplicatePair = {
  a: Issue;
  b: Issue;
  score: number;            // Jaccard 0..1
  shared: string[];         // gemeinsame Tokens (für Anzeige)
  sharedCount: number;
  uniqueA: number;
  uniqueB: number;
};

export type DetectOpts = {
  threshold: number;              // 0..1, Default 0.7
  minSharedTokens: number;        // Default 3 — gegen Kurz-Summary-Rauschen
  // Asymmetrischer Scope: die linke Seite (A) ist immer ein offenes Item;
  // die rechte Seite (B) darf offen ODER done (in einem Zeitfenster) sein.
  // So findet man für jedes offene Item alle relevanten Duplikat-Kandidaten,
  // ohne dass done↔done-Paare den Output fluten.
  bScope: 'open' | 'open+recent-done' | 'all';
  recentDoneCutoffMs?: number;    // bei bScope='open+recent-done': Resolved-Datum >= cutoff
  doneStatuses: Set<string>;      // typischerweise aus status.ts
  preFilter: 'team+type' | 'type-only' | 'none';
};

// Pre-Filter-Key für Bucketing. Ein Pair wird nur innerhalb desselben
// Buckets verglichen — drastisch reduzierter Vergleichs-Aufwand auf
// 21k Items.
function bucketKey(r: Issue, mode: DetectOpts['preFilter']): string {
  const type = r['Issue Type'] ?? '';
  const team = r['Team Name']  ?? '';
  if (mode === 'team+type') return `${team}::${type}`;
  if (mode === 'type-only') return type;
  return '*';
}

// Item ist offen, wenn der Status NICHT in den done-Statuses ist.
function isOpenItem(r: Issue, opts: DetectOpts): boolean {
  const status = (r['Status'] ?? '').trim().toLowerCase();
  return !opts.doneStatuses.has(status);
}

// Eligibility für die rechte Pair-Seite (B). A ist immer offen — wir
// suchen ja Duplikate FÜR offene Items.
function isBEligible(r: Issue, opts: DetectOpts): boolean {
  const isDone = !isOpenItem(r, opts);
  if (opts.bScope === 'open') return !isDone;
  if (opts.bScope === 'all')  return true;
  // 'open+recent-done'
  if (!isDone) return true;
  if (!opts.recentDoneCutoffMs) return false;
  const d = resolvedOf(r);
  if (!d) return false;
  return d.getTime() >= opts.recentDoneCutoffMs;
}

export type FindDuplicatesResult = {
  pairs: DuplicatePair[];
  // distincte offene Items, für die mind. ein Duplikat-Kandidat gefunden wurde
  affectedOpenKeys: Set<string>;
  // Anzahl offener Stories/Bugs im Input nach Pre-Filter (für „X von Y offen")
  totalOpenItems: number;
};

export function findDuplicates(rows: Issue[], opts: DetectOpts): FindDuplicatesResult {
  // 1) Eligible Items vorbereiten: pre-tokenisiert + Bucket-Key + isOpen-Flag.
  // Ein Item kommt rein, wenn es B-eligible ist — A ist eine Teilmenge davon
  // (alle offenen B-eligible Items sind A-eligible).
  type Prepared = {
    r: Issue;
    tokens: Set<string>;
    tokenCount: number;
    bucket: string;
    isOpen: boolean;
    system: string | null;
  };
  const prep: Prepared[] = [];
  let totalOpenItems = 0;
  for (const r of rows) {
    const t = r['Issue Type'];
    if (t !== 'Story' && t !== 'Bug') continue;
    if (!isBEligible(r, opts)) continue;
    const open = isOpenItem(r, opts);
    if (open) totalOpenItems++;
    const summary = r['Summary'] ?? '';
    if (!summary.trim()) continue;
    const toks = tokenize(summary);
    if (toks.length < opts.minSharedTokens) continue;
    const set = new Set(toks);
    prep.push({
      r,
      tokens: set,
      tokenCount: set.size,
      bucket: bucketKey(r, opts.preFilter),
      isOpen: open,
      system: extractSystem(summary),
    });
  }

  // 2) Buckets bilden
  const buckets = new Map<string, Prepared[]>();
  for (const p of prep) {
    let b = buckets.get(p.bucket);
    if (!b) { b = []; buckets.set(p.bucket, b); }
    b.push(p);
  }

  // 3) Pro Bucket alle Paare vergleichen.
  // Wichtig: nur Paare aufnehmen, bei denen MINDESTENS EINES offen ist —
  // wir suchen Duplikate FÜR offene Items, done↔done ist uninteressant.
  // Das offene Item wird als A normalisiert (sind beide offen, dann das
  // tokenärmere — bleibt deterministisch nach Sortier-Order).
  // Length-Pruning: |A∩B| ≤ min(|A|,|B|) und |A∪B| ≥ max(|A|,|B|), also
  // muss min/max ≥ threshold sein. Da items nach tokenCount aufsteigend
  // sortiert sind, ist immer min = items[i] und max = items[j].
  const pairs: DuplicatePair[] = [];
  const affectedOpenKeys = new Set<string>();
  const lengthRatioMin = opts.threshold;
  for (const items of buckets.values()) {
    if (items.length < 2) continue;
    items.sort((x, y) => x.tokenCount - y.tokenCount);
    for (let i = 0; i < items.length; i++) {
      const X = items[i];
      for (let j = i + 1; j < items.length; j++) {
        const Y = items[j];
        // Mindestens eines muss offen sein — sonst irrelevant.
        if (!X.isOpen && !Y.isOpen) continue;
        // Length-Pruning (X ist kürzer oder gleich lang als Y)
        if (X.tokenCount / Y.tokenCount < lengthRatioMin) continue;
        // System-Filter: wenn beide Tickets ein Produkt-Präfix haben und die
        // Präfixe unterschiedlich sind → kein Duplikat, unabhängig davon wie
        // ähnlich der Rest ist (Beispiel: „AWS 75 WF.SI+" vs „AWS 75.SI+").
        if (X.system && Y.system && X.system !== Y.system) continue;
        // Intersection
        let shared = 0;
        const sharedList: string[] = [];
        for (const t of X.tokens) {
          if (Y.tokens.has(t)) {
            shared++;
            if (sharedList.length < 8) sharedList.push(t);
          }
        }
        if (shared < opts.minSharedTokens) continue;
        const union = X.tokenCount + Y.tokenCount - shared;
        const score = shared / union;
        if (score < opts.threshold) continue;
        // A = das offene Item (wenn beide offen, X als kürzeres bleibt A)
        const aIsX = X.isOpen;
        const A = aIsX ? X : Y;
        const B = aIsX ? Y : X;
        pairs.push({
          a: A.r, b: B.r, score,
          shared: sharedList,
          sharedCount: shared,
          uniqueA: A.tokenCount - shared,
          uniqueB: B.tokenCount - shared,
        });
        affectedOpenKeys.add(A.r['Issue key'] ?? '');
        // Wenn beide offen sind, ist auch B ein „affected open" Item —
        // tracken für den Top-Counter.
        if (B.isOpen) affectedOpenKeys.add(B.r['Issue key'] ?? '');
      }
    }
  }

  // 4) Sortieren: Score DESC, dann sharedCount DESC
  pairs.sort((x, y) => (y.score - x.score) || (y.sharedCount - x.sharedCount));
  return { pairs, affectedOpenKeys, totalOpenItems };
}

// Helper: Cutoff für "letzte N Monate" — JS-Date arithmetic ist OK,
// brauchen keine Library für Monat-Subtraktion.
export function monthsAgo(months: number, now: Date = new Date()): number {
  const d = new Date(now);
  d.setMonth(d.getMonth() - months);
  return d.getTime();
}
